import { useCallback, useEffect, useRef, useState } from "react";
import { api, localPhotos, uploadedMap } from "./api";
import { renumber } from "./summary";

const collectLocal = (doc) => [...new Set(JSON.stringify(doc).match(/local_[a-z0-9]+/g) || [])];

async function uploadPending(doc, id) {
  const map = {};
  const store = localPhotos();
  for (const lid of collectLocal(doc)) {
    if (uploadedMap[lid]) { map[lid] = uploadedMap[lid]; continue; }
    if (!store[lid]) continue;
    const { data } = await api.post("/photos", { data_url: store[lid], inspection_id: id });
    map[lid] = data.photo_id;
    uploadedMap[lid] = data.photo_id;
  }
  return map;
}

const applyMap = (doc, map) => {
  let s = JSON.stringify(doc);
  Object.entries(map).forEach(([k, v]) => { s = s.split(k).join(v); });
  return JSON.parse(s);
};

export function useInspection(id) {
  const key = `pc_ins_${id}`;
  const [ins, setIns] = useState(null);
  const [status, setStatus] = useState("SYNCED");
  const [forceOffline, setForceOffline] = useState(localStorage.getItem("pc_force_offline") === "1");
  const latest = useRef(null);
  const busy = useRef(false);
  const dirty = useRef(false);
  const offlineRef = useRef(forceOffline);

  const sync = useCallback(async () => {
    if (!latest.current) return;
    if (!navigator.onLine || offlineRef.current) { setStatus("OFFLINE"); return; }
    if (busy.current) { dirty.current = true; return; }
    busy.current = true;
    setStatus("SYNCING");
    try {
      do {
        dirty.current = false;
        const map = await uploadPending(latest.current, id);
        if (Object.keys(map).length) {
          latest.current = applyMap(latest.current, map);
          const store = localPhotos();
          Object.keys(map).forEach((k) => delete store[k]);
          localStorage.setItem("pc_photos", JSON.stringify(store));
        }
        const d = latest.current;
        const { data } = await api.put(`/inspections/${id}`, {
          rooms: d.rooms, defects: d.defects, original_defects: d.original_defects, final_notes: d.final_notes, equipment: d.equipment,
        });
        latest.current = { ...latest.current, status: data.status, summary: data.summary };
      } while (dirty.current);
      localStorage.removeItem(key);
      setIns({ ...latest.current });
      setStatus("SYNCED");
    } catch (e) {
      setStatus(navigator.onLine ? "ERROR" : "OFFLINE");
    } finally {
      busy.current = false;
    }
  }, [id, key]);

  useEffect(() => {
    const cached = localStorage.getItem(key);
    if (cached) {
      const doc = JSON.parse(cached).doc;
      latest.current = doc;
      setIns(doc);
      sync();
      return;
    }
    api.get(`/inspections/${id}`).then(({ data }) => { latest.current = data; setIns(data); }).catch(() => setStatus("ERROR"));
  }, [id, key, sync]);

  useEffect(() => {
    const on = () => sync();
    const off = () => setStatus("OFFLINE");
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [sync]);

  const update = useCallback((fn) => {
    const next = fn(latest.current);
    next.defects = renumber(next.defects || []);
    latest.current = next;
    setIns(next);
    try { localStorage.setItem(key, JSON.stringify({ pending: true, doc: next })); } catch { /* quota */ }
    sync();
  }, [key, sync]);

  const toggleOffline = (v) => {
    offlineRef.current = v;
    setForceOffline(v);
    localStorage.setItem("pc_force_offline", v ? "1" : "0");
    if (!v) sync(); else setStatus("OFFLINE");
  };

  return { ins, update, status, forceOffline, toggleOffline, sync, setIns: (d) => { latest.current = d; setIns(d); } };
}
